// Days in a row, the quiet way (.wiki/wiki/concepts/quiet-retention.md, rule 3): any daily played counts, in any
// game, won or not; today still counts as open until midnight; and one missed day in any seven is forgiven without
// asking. Nothing here ever reports a lost run: a run of 0 or 1 simply isn't shown.
// Days are puzzle dates, as lib/daily.ts records them, so playing a missed day from the calendar fills it in.

/** Every date on which this device played at least one daily, from lib/daily.ts records. */
export function playedDays(recs: Record<string, Record<string, string>>): Set<string> {
  const days = new Set<string>()
  for (const game of Object.values(recs)) for (const date of Object.keys(game)) days.add(date)
  return days
}

const shift = (iso: string, by: number): string => {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number]
  const t = new Date(Date.UTC(y, m - 1, d + by))
  return t.toISOString().slice(0, 10)
}

/** Played days in the run ending today (or yesterday, while today is still open). A missed day is bridged when no
 *  other bridge falls in the six days before it; two missed days in a row end the run. Bridged days don't count. */
export function runLength(days: Set<string>, today: string): number {
  // Not played yet today: the run is still open, so yesterday may be the forgiven day.
  const open = !days.has(today)
  let day = open ? shift(today, -1) : today
  let run = 0
  let lastBridge: string | null = null
  for (let guard = 0; guard < 3660; guard++) {
    if (days.has(day)) { run++; day = shift(day, -1); continue }
    const before = shift(day, -1)
    // A bridge needs a played day on its far side, and none within the previous week of the last one.
    if ((run > 0 || open) && days.has(before) && (lastBridge === null || lastBridge > shift(day, 6))) {
      lastBridge = day
      day = before
      continue
    }
    break
  }
  return run
}

/** How many days in `month` (YYYY-MM) have a daily played. */
export const daysInMonth = (days: Set<string>, month: string): number => [...days].filter((d) => d.startsWith(`${month}-`)).length
